/**
 * Layer 1 of the manifest: the capability card, a short text every harness
 * seat puts in its cached prompt prefix. Rendered from the manifest by code,
 * never written by hand, so a part added in code reaches the prompt in the
 * same release. Pure and byte-stable for a version: no date, no count that is
 * not the manifest's, entries in `CAPABILITY_IDS` order.
 *
 * In order: the header with the version; one line per level of what a setup
 * builds today, with the limits that change a design; what only OS adds after
 * Build; every "not possible yet" with its closest fit; and `NOT_YET_RULE`,
 * how to say so.
 */
import { CAPABILITY_LEVELS, type CapabilityLevel } from './ids';
import { buildManifest, type CapabilityEntry, type CapabilityManifest } from './manifest';

/**
 * How every seat says "not possible yet" (decision 1 of
 * docs/plans/onboarding-harness.md): once, with the closest fit, noted, and
 * never naming another product.
 */
export const NOT_YET_RULE =
  "When the person asks for something RecordOS cannot do yet (the list above, or anything no part holds), say so once, in one short sentence, in your own words: that RecordOS can't do that yet. Never draw, promise or pretend a part for it. Then make the closest fit from the parts and say what you made. Note the wish once (note_unmet_need, or the note the setup tool takes), and you may say it is noted so they hear when RecordOS can do it. Never say it again for the same wish in this conversation. Never name another app or product, even when the person named it: say 'the app you use'.";

const LEVEL_NAME: Readonly<Record<CapabilityLevel, string>> = {
  workspace: 'Workspace',
  data: 'Data',
  surface: 'How entries are written',
  arrangement: 'Where fields sit',
  lifecycle: 'The life of an entry',
  view: 'How entries are seen',
  insight: 'Cards',
  automation: 'Links',
  people: 'People',
  memory: 'Memory',
  input: 'What comes in',
};

const limit = (byId: ReadonlyMap<string, CapabilityEntry>, id: string, name: string): number | undefined => byId.get(id)?.limits?.[name];

/** The limits that change a design, per level, from the entries' own limits. */
const levelLimits = (level: CapabilityLevel, byId: ReadonlyMap<string, CapabilityEntry>): string[] => {
  const say = (sentence: string, ...values: (number | undefined)[]): string[] => (values.every((one) => one !== undefined) ? [sentence] : []);
  const at = (id: string, name: string) => limit(byId, id, name);
  switch (level) {
    case 'data':
      return [
        ...say(`Up to ${at('record.record', 'records')} records build in one setup, ${at('record.record', 'fields')} fields a record.`, at('record.record', 'records'), at('record.record', 'fields')),
        ...say(
          `A record totals at most ${at('record.record', 'n')} number fields and holds at most ${at('record.record', 'r')} choices or picks from a list; text, dates and yes-or-no never run out.`,
          at('record.record', 'n'),
          at('record.record', 'r'),
        ),
        ...say(
          `Lines: ${at('field.type.lines', 'fields')} fields a line, ${at('field.type.lines', 'depth')} levels, ${at('field.type.lines', 'perEntry')} lines an entry.`,
          at('field.type.lines', 'fields'),
          at('field.type.lines', 'depth'),
          at('field.type.lines', 'perEntry'),
        ),
        ...say(`A choice holds ${at('field.type.select', 'optionsMin')} to ${at('field.type.select', 'options')} options.`, at('field.type.select', 'optionsMin'), at('field.type.select', 'options')),
        ...say(`A list starts with up to ${at('record.seed_rows', 'rows')} rows.`, at('record.seed_rows', 'rows')),
      ];
    case 'arrangement':
      return say(`Up to ${at('arrangement.sections', 'sections')} sections a form, one level inside.`, at('arrangement.sections', 'sections'));
    case 'view':
      return say(`Up to ${at('view.summary', 'cards')} cards a record.`, at('view.summary', 'cards'));
    case 'insight':
      return say(`Up to ${at('insight.where', 'where')} conditions a card.`, at('insight.where', 'where'));
    case 'automation':
      return say(
        `Up to ${at('link.and', 'links')} links a setup, ${at('link.and', 'when')} conditions and ${at('link.and', 'carry')} fields carried a link.`,
        at('link.and', 'links'),
        at('link.and', 'when'),
        at('link.and', 'carry'),
      );
    default:
      return [];
  }
};

const canName = (entry: CapabilityEntry): string => (entry.id.startsWith('field.type.') ? entry.id.slice('field.type.'.length) : entry.name);

/**
 * The card's "Not possible yet" lines, each ask with its closest fit: what
 * every seat that suggests (the turn's card, OS's critic) must never offer.
 * Two entries may name the same ask (a pattern with no engine, and the ask it
 * answers): said once.
 */
export const notYetLines = (manifest: CapabilityManifest): string[] => {
  const said = new Set<string>();
  for (const entry of manifest.entries) {
    if (entry.cannotYet === undefined) continue;
    said.add(`- ${entry.cannotYet.need} → closest fit: ${entry.cannotYet.fit}`);
  }
  return [...said];
};

/** The card for a manifest. Pure; the same manifest renders the same bytes. */
export const renderCapabilityCard = (manifest: CapabilityManifest): string => {
  const byId = new Map(manifest.entries.map((entry) => [entry.id, entry]));
  const lines: string[] = [`What RecordOS can make (manifest ${manifest.version}). Build only from these parts.`, ''];

  for (const level of CAPABILITY_LEVELS) {
    const can = manifest.entries.filter((entry) => entry.level === level && entry.status === 'shipped' && entry.reach.setup === 'yes');
    if (can.length === 0) continue;
    const types = can.filter((entry) => entry.id.startsWith('field.type.'));
    const rest = can.filter((entry) => !entry.id.startsWith('field.type.'));
    const parts = [
      ...(types.length === 0 ? [] : [`field types ${types.map(canName).join(', ')}`]),
      ...rest.map(canName),
    ];
    lines.push([`${LEVEL_NAME[level]}: ${parts.join('; ')}.`, ...levelLimits(level, byId)].join(' '));
  }

  const after = manifest.entries.filter((entry) => entry.status === 'shipped' && entry.reach.setup === 'no' && entry.reach.os.length > 0);
  if (after.length > 0) {
    lines.push('', `After Build only: ${after.map((entry) => entry.name).join('; ')}. Say these can be added right after Build; never draw them in the setup.`);
  }

  const notYet = notYetLines(manifest);
  if (notYet.length > 0) lines.push('', 'Not possible yet:', ...notYet);

  lines.push('', NOT_YET_RULE);
  return lines.join('\n');
};

/** The card of the manifest this build ships. */
export const CAPABILITY_CARD = renderCapabilityCard(buildManifest());

/** The "Not possible yet" lines of the manifest this build ships, as the card says them. */
export const CAPABILITY_NOT_YET: readonly string[] = notYetLines(buildManifest());
